import {
	type ButtonProps,
	HStack,
	Icon,
	IconButton,
	Spinner,
	Stack,
	StackSeparator,
} from "@chakra-ui/react";
import { CloseInstructionsIcon } from "components/Icons/CloseInstructionsIcon";
import { CopyIcon } from "components/Icons/CopyIcon";
import { NewTabIcon } from "components/Icons/NewTabIcon";
import { Button } from "components/ui/button";
import { Tooltip } from "components/ui/tooltip";
import COLORS from "constants/colors";
import { useClipboard, useClipboardPermission } from "hooks/use-clipboard";
import { useEffect, useRef } from "react";
import { usePopOutInstructionsLink } from "store/pop-out-instructions";
import { useInstructionsHtml } from "../../../services/instructions-html";
import { IsolatedContent } from "./IsolatedContent";

const PlainButton = (
	props: ButtonProps & { href?: string | null; target?: string },
) => (
	<Button
		variant="plain"
		fontSize="13px"
		css={{ "& :where(svg)": { boxSize: "16px" } }}
		color={COLORS.GRAY.GRAY_600}
		_hover={{ color: COLORS.BLACK }}
		h="20px"
		p="0"
		{...props}
	/>
);

export const InstructionsPopout = () => {
	const {
		popOutInstructionsLink,
		popOutInstructionsInnerHtmlToScroll,
		popOutInstructionsAugmentation,
		setPopOutInstructionsLink,
	} = usePopOutInstructionsLink();

	const { html, isPending } = useInstructionsHtml({
		instructionsLink: popOutInstructionsLink,
	});

	const scrollContainerRef = useRef<HTMLDivElement>(null);

	const { copied, copy } = useClipboard(popOutInstructionsLink ?? "");

	const { data: clipboardPermission } = useClipboardPermission();

	const InstructionsAugmentation =
		popOutInstructionsAugmentation?.component;

	useEffect(() => {
		if (!popOutInstructionsInnerHtmlToScroll || !html || isPending) {
			return;
		}

		// Wait a bit for the HTML to be fully rendered
		const timeoutId = setTimeout(() => {
			let retryCount = 0;
			const maxRetries = 10;

			const attemptScroll = () => {
				const container = scrollContainerRef.current;
				if (!container) {
					if (retryCount < maxRetries) {
						retryCount++;
						requestAnimationFrame(attemptScroll);
					}
					return;
				}

				let element: HTMLElement | null = null;

				// Prioritize innerHtmlToScroll search
				if (popOutInstructionsInnerHtmlToScroll) {
					const searchText = popOutInstructionsInnerHtmlToScroll.trim();
					
					// Helper function to find the most specific element with the text
					const findElementByText = (elements: Element[]): HTMLElement | null => {
						let bestMatch: HTMLElement | null = null;
						let bestMatchLength = Infinity;
						
						for (const el of elements) {
							const textContent = el.textContent?.trim() || '';
							
							// Look for elements that start with the search text (most specific)
							// or contain it as the primary content
							if (textContent.startsWith(searchText) || textContent === searchText) {
								// Prefer exact matches or shorter text (more specific)
								if (textContent.length < bestMatchLength) {
									bestMatch = el as HTMLElement;
									bestMatchLength = textContent.length;
								}
							}
						}
						
						return bestMatch;
					};
					
					// Search within the container first
					const containerElements = Array.from(container.querySelectorAll("*"));
					element = findElementByText(containerElements);
					
					// If not found in container, search document-wide
					if (!element) {
						const allElements = Array.from(document.querySelectorAll("*"));
						element = findElementByText(allElements);
					}
				}

				if (element) {
					// Ensure element is within the container
					if (!container.contains(element)) {
						// Find the closest ancestor that's within the container
						let parent = element.parentElement;
						while (parent && !container.contains(parent)) {
							parent = parent.parentElement;
						}
						if (parent && container.contains(parent)) {
							element = parent as HTMLElement;
						} else {
							setPopOutInstructionsLink(popOutInstructionsLink, null);
							return;
						}
					}
					
					// Use getBoundingClientRect to calculate scroll position
					const containerRect = container.getBoundingClientRect();
					const elementRect = element.getBoundingClientRect();
					
					const scrollTop = container.scrollTop + (elementRect.top - containerRect.top);
					
					// Use the calculated scroll position without extra offset
					const finalScrollTop = Math.max(0, scrollTop);
					
					container.scrollTo({
						top: finalScrollTop,
						behavior: "smooth",
					});
					
					setPopOutInstructionsLink(popOutInstructionsLink, null);
				} else if (retryCount < maxRetries) {
					retryCount++;
					requestAnimationFrame(attemptScroll);
				} else {
					setPopOutInstructionsLink(popOutInstructionsLink, null);
				}
			};

			requestAnimationFrame(attemptScroll);
		}, 200);

		return () => clearTimeout(timeoutId);
	}, [
		popOutInstructionsInnerHtmlToScroll,
		html,
		isPending,
		popOutInstructionsLink,
		setPopOutInstructionsLink,
	]);

	return (
		<Stack
			w="full"
			separator={<StackSeparator />}
			justify="flex-start"
			h="full"
			gap="0"
		>
			<HStack justify="space-between" px="20px" py="16px">
				<Tooltip
					content="Close instructions"
					openDelay={250}
					positioning={{ placement: "right-end" }}
					contentProps={{ fontSize: "13px", borderRadius: "6px" }}
				>
					<IconButton
						boxSize="24px"
						minW="24px"
						variant="ghost"
						aria-label="Close Instructions"
						onClick={() => setPopOutInstructionsLink(null)}
						css={{ "& :where(svg)": { boxSize: "17px" } }}
						color={COLORS.GRAY.GRAY_600}
						_hover={{ color: COLORS.BLACK }}
					>
						<CloseInstructionsIcon />
					</IconButton>
				</Tooltip>
				<HStack gap="4">
					{(clipboardPermission?.state === "granted" ||
						clipboardPermission?.state === "prompt") && (
						<PlainButton onClick={() => copy()} p="0">
							<Icon boxSize="16px">
								<CopyIcon />
							</Icon>
							{copied ? "Copied" : "Copy link to instructions"}
						</PlainButton>
					)}
					<PlainButton
						as="a"
						href={popOutInstructionsLink}
						target="_blank"
						rel="noreferrer"
					>
						<Icon>
							<NewTabIcon />
						</Icon>
						Open in new tab
					</PlainButton>
				</HStack>
			</HStack>
			<Stack
				ref={scrollContainerRef}
				px="20px"
				h="full"
				pb="20px"
				w="full"
				align="stretch"
				justify="flex-start"
				gap="24px"
				overflowY="auto"
				_scrollbar={{ w: "6px" }}
				_scrollbarThumb={{ bg: COLORS.GRAY.GRAY_400, borderRadius: "2px" }}
				_scrollbarTrack={{ px: "10px", bg: COLORS.WHITE }}
			>
				{isPending && (
					<HStack justify="center" w="full" py="16px">
						<Spinner />
					</HStack>
				)}
				{html && InstructionsAugmentation && <InstructionsAugmentation />}
				{html && <IsolatedContent html={html} />}
			</Stack>
		</Stack>
	);
};
